// hooks/useVideoManager.js
import { useCallback } from "react";
import { useVideo } from "../context/VideoContext";
import useSubtitleManager from "./useSubtitleManager";
import {
  findCollectionSubtitlesByName,
  fetchSubtitleContent,
  findSubtitlesWithId,
} from "../services/subtitleApi";
import useVideoMetadata from "./useVideoMetadata";
import { safeGetItem, safeSetItem } from "../utils/safeStorage";
import { useSelectedSubtitles } from "../context/SelectedSubtitlesContext";
import { getLanguageName } from "../utils/languages";

const useVideoManager = () => {
  const {
    handleFileSelect,
    videoRef,
    setSubtitle1Text,
    setSubtitle2Text,
    setSubtitle1Loaded,
    setSubtitle2Loaded,
    setSubtitle1Json,
    setSubtitle2Json,
    setIsLoadingSubtitles,
    setLoadingMessage,
    removeSubtitle,
    setShowSubtitleNotFoundModal,
    setCurrentMovieName,
    setShowOtherUsersSubtitles,
    setOtherUsersSubtitlesData,
    setSubtitle1Delay,
    setSubtitle2Delay,
    subtitle1Delay,
    subtitle2Delay,
    setAutoLoadedSub1Id,
    setAutoLoadedSub2Id,
    setShowTutorialGuide,
  } = useVideo();

  const { setupSubtitle, clearAllTracks } = useSubtitleManager(videoRef);
  const videoMetadataManager = useVideoMetadata();
  const { setActiveSubtitle } = useSelectedSubtitles();

  // 🎯 تابع کمکی برای دریافت اطلاعات کامل یک زیرنویس آنلاین
  const fetchSubtitleFullInfo = useCallback(async (subtitlesUploadId) => {
    if (!subtitlesUploadId) return null;

    try {
      const result = await findSubtitlesWithId(subtitlesUploadId);
      if (result.success && result.data) {
        return result.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching subtitle full info:", error);
      return null;
    }
  }, []);

  // تابع کمکی برای ریست تاخیر از روی cueها
  const resetDelayFromCues = useCallback(
    (subNumber) => {
      const video = videoRef.current;
      if (!video) return;

      // پیدا کردن track مناسب
      const textTracks = video.textTracks;
      let targetTrack = null;

      for (let i = 0; i < textTracks.length; i++) {
        const track = textTracks[i];
        if (
          subNumber === 1 &&
          (track.language === "en" || track.id === "subtitle-track-1")
        ) {
          targetTrack = track;
          break;
        } else if (
          subNumber === 2 &&
          (track.language === "fa" || track.id === "subtitle-track-2")
        ) {
          targetTrack = track;
          break;
        }
      }

      if (!targetTrack || !targetTrack.cues) return;

      const currentDelay = subNumber === 1 ? subtitle1Delay : subtitle2Delay;

      if (currentDelay !== 0) {
        Array.from(targetTrack.cues).forEach((cue) => {
          cue.startTime -= currentDelay / 1000;
          cue.endTime -= currentDelay / 1000;
        });
      }
    },
    [videoRef, subtitle1Delay, subtitle2Delay],
  );

  // تابع پاک کردن کامل زیرنویس‌ها
  const clearAllSubtitles = useCallback(() => {
    clearAllTracks();

    setSubtitle1Text("");
    setSubtitle2Text("");
    setSubtitle1Loaded(false);
    setSubtitle2Loaded(false);
    setSubtitle1Json([]);
    setSubtitle2Json([]);
    setSubtitle1Delay(0);
    setSubtitle2Delay(0);
    setAutoLoadedSub1Id(null);
    setAutoLoadedSub2Id(null);

    window.dispatchEvent(new CustomEvent("subtitle1change", { detail: "" }));
    window.dispatchEvent(new CustomEvent("subtitle2change", { detail: "" }));
  }, [
    clearAllTracks,
    setSubtitle1Text,
    setSubtitle2Text,
    setSubtitle1Loaded,
    setSubtitle2Loaded,
    setSubtitle1Json,
    setSubtitle2Json,
    setSubtitle1Delay,
    setSubtitle2Delay,
  ]);

  // 🎯 تابع loadSubtitlesFromIds اصلاح‌شده
  const loadSubtitlesFromIds = useCallback(
    async (subOneID, subTwoID, langOne, langTwo, collectionInfo) => {
      try {
        // 🎯 استخراج coverUrl و imdbId از collectionInfo (ریسپانس exactly)
        const collectionCoverUrl = collectionInfo?.coverUrl || "";
        const collectionImdbId =
          collectionInfo?.imdbId || collectionInfo?.imdb_id || "";

        // 🎯 تابع کمکی برای ساخت metadata با coverUrl و imdbId
        const buildMetadata = (subNumber, lang, extraInfo = {}) => ({
          sourceType: "online",
          onlineId: subNumber === 1 ? subOneID : subTwoID,
          subtitlesUploadId: subNumber === 1 ? subOneID : subTwoID,
          language: lang,
          movieName:
            collectionInfo?.movieName || collectionInfo?.nameOfMovie || "",
          videoType: collectionInfo?.videoType || "movie",
          season: collectionInfo?.season?.toString() || "",
          episode: collectionInfo?.episode?.toString() || "",
          subOneID: collectionInfo?.subOneID,
          subTwoID: collectionInfo?.subTwoID,
          langOne: collectionInfo?.langOne,
          langTwo: collectionInfo?.langTwo,
          collectionId: collectionInfo?.collectionId,
          movieFileName: videoMetadataManager.videoMetadata?.fileName || "",
          // 🎯 اینا از collectionInfo یا fetchFullInfo پر میشن
          coverUrl: extraInfo.coverUrl || collectionCoverUrl || "",
          imdbId:
            extraInfo.imdbId || extraInfo.imdb_id || collectionImdbId || "",
          _source: "backend",
        });

        // لود زیرنویس اول
        if (subOneID) {
          setLoadingMessage(
            `در حال بارگذاری زیرنویس ${getLanguageName(langOne)}...`,
          );

          const content = await fetchSubtitleContent(subOneID);
          const file = new File([content], `subtitle1.${langOne}.srt`, {
            type: "text/plain",
          });

          // 🎯 اول با collectionInfo می‌سازیم
          let metadata1 = buildMetadata(1, langOne);

          // 🎯 اگه coverUrl یا imdbId نداشتیم، با fetchFullInfo تکمیل کن
          if (!metadata1.coverUrl || !metadata1.imdbId) {
            const fullInfo = await fetchSubtitleFullInfo(subOneID);
            if (fullInfo) {
              metadata1 = buildMetadata(1, langOne, fullInfo);
            }
          }

          const success = await setupSubtitle(
            file,
            langOne,
            1,
            (text) => {
              setSubtitle1Text(text);
              window.dispatchEvent(
                new CustomEvent("subtitle1change", { detail: text }),
              );
            },
            metadata1,
          );

          if (success) {
            setSubtitle1Loaded(true);

            // 🎯 ست کردن در SelectedSubtitlesContext (خودش به تاریخچه اضافه می‌کند)
            await setActiveSubtitle(metadata1, 1, file);
          }
        }

        // لود زیرنویس دوم
        if (subTwoID) {
          setLoadingMessage(
            `در حال بارگذاری زیرنویس ${getLanguageName(langTwo)}...`,
          );

          const content = await fetchSubtitleContent(subTwoID);
          const file = new File([content], `subtitle2.${langTwo}.srt`, {
            type: "text/plain",
          });

          // 🎯 اول با collectionInfo می‌سازیم
          let metadata2 = buildMetadata(2, langTwo);

          // 🎯 اگه coverUrl یا imdbId نداشتیم، با fetchFullInfo تکمیل کن
          if (!metadata2.coverUrl || !metadata2.imdbId) {
            const fullInfo = await fetchSubtitleFullInfo(subTwoID);
            if (fullInfo) {
              metadata2 = buildMetadata(2, langTwo, fullInfo);
            }
          }

          const success = await setupSubtitle(
            file,
            langTwo,
            2,
            (text) => {
              setSubtitle2Text(text);
              window.dispatchEvent(
                new CustomEvent("subtitle2change", { detail: text }),
              );
            },
            metadata2,
          );

          if (success) {
            setSubtitle2Loaded(true);

            // 🎯 ست کردن در SelectedSubtitlesContext (خودش به تاریخچه اضافه می‌کند)
            await setActiveSubtitle(metadata2, 2, file);
          }
        }
      } catch (error) {
        console.error("Error loading subtitles from IDs:", error);
        throw error;
      }
    },
    [
      setupSubtitle,
      setSubtitle1Text,
      setSubtitle2Text,
      setSubtitle1Loaded,
      setSubtitle2Loaded,
      setLoadingMessage,
      setActiveSubtitle,
      fetchSubtitleFullInfo,
      videoMetadataManager,
    ],
  );

  // تابع نرمال‌سازی زیرنویس برای تاریخچه
  const normalizeSubtitleForHistory = useCallback(
    (subtitleInfo, id, language, targetNumber) => {
      return {
        _id: id,
        subtitlesLanguage: language,
        language: language,
        nameOfMovie: subtitleInfo.movieName || "نامشخص",
        season: subtitleInfo.season?.toString() || "",
        episode: subtitleInfo.episode?.toString() || "",
        videoType: subtitleInfo.videoType || "movie",
        source: "auto-load",
        targetNumber,
        addedAt: new Date().toISOString(),
        // نگه داشتن اطلاعات اضافی
        collectionId: subtitleInfo.collectionId,
        subOneID: subtitleInfo.subOneID,
        subTwoID: subtitleInfo.subTwoID,
        langOne: subtitleInfo.langOne,
        langTwo: subtitleInfo.langTwo,
      };
    },
    [],
  );

  // 🎯 تابع sendMovieNameToBackend اصلاح‌شده
  const sendMovieNameToBackend = useCallback(
    async (fileName) => {
      try {
        setLoadingMessage("در حال دریافت اطلاعات زیرنویس...");
        setCurrentMovieName(fileName);

        // استخراج اطلاعات از نام فایل
        const extractedInfo =
          videoMetadataManager.extractInfoFromFileName(fileName);
        videoMetadataManager.setMetadata(extractedInfo);

        const data = await findCollectionSubtitlesByName(fileName);

        // بررسی حالت exactly
        if (
          data.success &&
          (data.moreInfo?.match === "exactly" ||
            data.moreInfo?.match === "exactlyByUser") &&
          data.data?.docs?.length > 0
        ) {
          const subtitleInfo = data.data.docs[0];

          // 🎯 به‌روزرسانی metadata با اطلاعات دریافتی از سرور (شامل coverUrl و imdbId)
          videoMetadataManager.updateMetadataFromBackend(data, fileName);

          // 🎯 ذخیره آی‌دی‌های لود اتوماتیک (برای آیکون ذخیره)
          setAutoLoadedSub1Id(subtitleInfo.subOneID);
          setAutoLoadedSub2Id(subtitleInfo.subTwoID);

          // 🎯 لود زیرنویس‌ها - حالا subtitleInfo شامل coverUrl و imdbId هم میشه
          await loadSubtitlesFromIds(
            subtitleInfo.subOneID,
            subtitleInfo.subTwoID,
            subtitleInfo.langOne,
            subtitleInfo.langTwo,
            subtitleInfo, // این کل ریسپانس داک رو شامل میشه
          );

          // 🎯 نمایش راهنما در صورت عدم تیک خوردن "دیگر نشان نده"
          const tutorialDismissed = safeGetItem("tutorialGuideDismissedAt");
          if (!tutorialDismissed) {
            setShowTutorialGuide(true);
          }

          return true;
        } else if (
          data.success &&
          data.moreInfo?.match === "exactlyByAnotherUser" &&
          data.data?.docs?.length > 0
        ) {
          // 🎯 حالت exactlyByAnotherUser: مودال یکپارچه با آکوردین پیشنهادها
          // داده‌های پیشنهادی رو در state نگه می‌داریم و مودال زیرنویس پیدا نشد رو باز می‌کنیم
          setOtherUsersSubtitlesData(data.data.docs);

          const subtitleSetting = safeGetItem(
            "subtitleNotFoundSetting",
          );
          if (subtitleSetting !== "dont-show") {
            setShowSubtitleNotFoundModal(true);
          }
          return true;
        } else {
          const subtitleSetting = safeGetItem(
            "subtitleNotFoundSetting",
          );
          if (subtitleSetting !== "dont-show") {
            setShowSubtitleNotFoundModal(true);
          }

          return false;
        }
      } catch (error) {
        console.error("Error sending movie name:", error);

        const subtitleSetting = safeGetItem("subtitleNotFoundSetting");
        if (subtitleSetting !== "dont-show") {
          setShowSubtitleNotFoundModal(true);
        }

        return false;
      }
    },
    [
      loadSubtitlesFromIds,
      setLoadingMessage,
      setCurrentMovieName,
      setShowSubtitleNotFoundModal,
      setShowOtherUsersSubtitles,
      setOtherUsersSubtitlesData,
      setShowTutorialGuide,
      videoMetadataManager,
    ],
  );

  const handleFileProcessing = useCallback(
    async (file) => {
      if (!file) return;

      setIsLoadingSubtitles(true);
      setLoadingMessage("در حال آماده‌سازی...");

      try {
        // 1. پاک کردن همه زیرنویس‌ها
        clearAllSubtitles();

        // 2. پخش فیلم جدید
        handleFileSelect(file);

        // 3. صبر برای لود ویدیو
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // 4. لود زیرنویس‌های جدید
        await sendMovieNameToBackend(file.name);
      } catch (error) {
        console.error("Error in file processing:", error);
      } finally {
        setTimeout(() => {
          setIsLoadingSubtitles(false);
          setLoadingMessage("");
        }, 500);
      }
    },
    [
      clearAllSubtitles,
      handleFileSelect,
      sendMovieNameToBackend,
      setIsLoadingSubtitles,
      setLoadingMessage,
    ],
  );

  // تابع loadLocalSubtitle
  const loadLocalSubtitle = useCallback(
    async (file, language, number, extraMetadata = {}) => {
      setIsLoadingSubtitles(true);
      setLoadingMessage("در حال بارگذاری زیرنویس...");

      try {
        resetDelayFromCues(number);

        if (number === 1) {
          setSubtitle1Delay(0);
        } else {
          setSubtitle2Delay(0);
        }

        if (number === 1) {
          setSubtitle1Text("");
          window.dispatchEvent(
            new CustomEvent("subtitle1change", { detail: "" }),
          );
        } else {
          setSubtitle2Text("");
          window.dispatchEvent(
            new CustomEvent("subtitle2change", { detail: "" }),
          );
        }

        removeSubtitle(number);

        const currentMetadata = videoMetadataManager.videoMetadata;

        // 🎯 اولویت با extraMetadata (اطلاعات واقعی زیرنویس)
        const subtitleMetadata = {
          // اطلاعات پایه
          sourceType: extraMetadata.sourceType || "local",
          language: language,
          fileName: file.name,

          // اطلاعات آنلاین
          onlineId:
            extraMetadata.onlineId || extraMetadata.subtitlesUploadId || null,
          subtitlesUploadId:
            extraMetadata.subtitlesUploadId || extraMetadata.onlineId || null,

          // اطلاعات فیلم - اولویت با extraMetadata
          nameOfMovie: extraMetadata.nameOfMovie || currentMetadata.filmName,
          movieName:
            extraMetadata.nameOfMovie ||
            extraMetadata.movieName ||
            currentMetadata.filmName,
          videoType: extraMetadata.videoType || currentMetadata.videoType,
          season: extraMetadata.season || currentMetadata.season,
          episode: extraMetadata.episode || currentMetadata.episode,
          movieFileName:
            extraMetadata.movieFileName || currentMetadata.fileName,

          // اطلاعات اضافی
          otherTags: extraMetadata.otherTags || [],
          productionYear: extraMetadata.productionYear || "",
          coverUrl: extraMetadata.coverUrl || "",

          // اگر از مقایسه اومده
          _source: extraMetadata._source || "comparison",
        };

        const success = await setupSubtitle(
          file,
          language,
          number,
          (text) => {
            if (number === 1) {
              setSubtitle1Text(text);
              window.dispatchEvent(
                new CustomEvent("subtitle1change", { detail: text }),
              );
            } else {
              setSubtitle2Text(text);
              window.dispatchEvent(
                new CustomEvent("subtitle2change", { detail: text }),
              );
            }
          },
          subtitleMetadata,
        );

        if (success) {
          if (number === 1) {
            setSubtitle1Loaded(true);
          } else {
            setSubtitle2Loaded(true);
          }

          // ذخیره در localStorage فقط برای زیرنویس‌های غیرآنلاین
          if (
            !subtitleMetadata.onlineId &&
            !subtitleMetadata.subtitlesUploadId
          ) {
            const subtitleData = {
              source: subtitleMetadata.sourceType,
              fileName: file.name,
              language: language,
              movieFileName: subtitleMetadata.movieFileName,
              nameOfMovie: subtitleMetadata.nameOfMovie,
              season: subtitleMetadata.season,
              episode: subtitleMetadata.episode,
              size: file.size,
              addedAt: new Date().toISOString(),
            };

            try {
              const localHistory = JSON.parse(
                safeGetItem("localSubtitlesHistory") || "[]",
              );
              localHistory.push({ ...subtitleData, id: `local-${Date.now()}` });
              safeSetItem(
                "localSubtitlesHistory",
                JSON.stringify(localHistory.slice(-50)),
              );
            } catch (storageError) {
              console.warn("Could not save to localStorage:", storageError);
            }
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingSubtitles(false);
        setLoadingMessage("");
      }
    },
    [
      setupSubtitle,
      removeSubtitle,
      setSubtitle1Text,
      setSubtitle2Text,
      setSubtitle1Loaded,
      setSubtitle2Loaded,
      setSubtitle1Delay,
      setSubtitle2Delay,
      resetDelayFromCues,
      videoMetadataManager,
      setIsLoadingSubtitles,
      setLoadingMessage,
    ],
  );

  return {
    handleFileProcessing,
    clearAllSubtitles,
    loadLocalSubtitle,
    loadSubtitlesFromIds,
    videoMetadata: videoMetadataManager.videoMetadata,
    extractInfoFromFileName: videoMetadataManager.extractInfoFromFileName,
  };
};

export default useVideoManager;
